import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReportIcon from '@mui/icons-material/Report';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DialogContentText } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import enGB from 'date-fns/locale/en-GB';
import parsePhoneNumber from 'libphonenumber-js';
import { orderBy, debounce } from 'lodash-es';
import React, { useEffect, useState, useCallback } from 'react';
import { PlusIcon } from 'lucide-react';
import {
	ConditionalWrapper,
	DataGrid,
	FilterAutoCompleteById,
	FilterSearch,
	FilterSelect,
	RoleChip,
	TaskDialog,
	AddToRoleDialog,
	ConfirmDialog,
	DocumentViewer,
	FormDialog,
	Badge,
	GeneralTooltip as Tooltip,
	Spinner,
} from '@repo/components';
import {
	Document,
	PERMISSIONS,
	Role,
	Task,
	ImportData,
	InviteData,
	Profile,
	User,
	RoleWithTasks,
} from '@repo/shared/domain';
import { InviteForm, UserForm } from '@repo/forms';
import {
	EventEmitter,
	guardedClient as request,
	sortToString,
	stringToSort,
	constructQuery,
	getVerifyPermission,
	importUserFailureFromCaughtError,
} from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import {
	getCompany,
	getCompanyUsers,
	getCompanyRoles,
	getCompanyUserTasks,
	getCompanyUserApprovals,
	getCompanyUserDocuments,
	getCompanyUserProfile,
	getCompanyUserRolesWithTasks,
} from 'src/api/company';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { useConfig } from 'src/hooks/useConfig';
import { calculateCascadingRoleRevocations } from 'src/helpers/calculateCascadingRoleRevocations';
import { FileState } from 'src/parts/tabs/DocumentTab';
import UserRolesWithTasksAccordion from 'src/parts/users/UserRolesWithTasksAccordion';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/users/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		search: (search.search as string) || '',
		page: (search.page as number) || 0,
		sort: (search.sort as string) || 'updatedAt:desc',
		roleIds: (search.roleIds as string) || '',
		roleStatus: (search.roleStatus as string) || 'all',
	}),
});

const variants: Record<string, string> = {
	done: 'success',
	accepted: 'success',
	pending: 'default',
	todo: 'default',
	expiryWarning: 'warning',
	expired: 'error',
	failed: 'error',
	inReview: 'inReview',
	declined: 'error',
};

const roleStatusTypes = [
	{ value: 'all', label: 'All' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'accepted', label: 'Accepted' },
	{ value: 'expired', label: 'Expired' },
	{ value: 'expiryWarning', label: 'Expiry Warning' },
];

const baseColumns = [
	{
		field: 'markedForReview',
		headerName: '',
		renderCell: (c: any) => c.row.markedForReview && <ReportIcon fontSize="small" color="error" />,
		width: 5,
	},
	{ field: 'firstName', headerName: 'First name', flex: 1 },
	{ field: 'lastName', headerName: 'Last name', flex: 1 },
	{
		field: 'roles',
		headerName: 'Roles',
		flex: 1,
		renderCell: (c: any) =>
			c.value.map((r: any) => (
				<RoleChip key={r.name} role={r} userMarkedForReview={c.row.markedForReview || c.row.status === 'blocked'} />
			)),
		sortable: false,
	},
	{
		field: 'updatedAt',
		headerName: 'Updated',
		width: 120,
		valueFormatter: (params: any) => new Date(params.value).toLocaleDateString('en-GB'),
	},
];

function RouteComponent() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({
		page: searchParams.page,
		sort: searchParams.sort,
		search: searchParams.search || undefined,
		roleIds: searchParams.roleIds || undefined,
		roleStatus: searchParams.roleStatus !== 'all' ? searchParams.roleStatus : undefined,
	});

	const { data: group } = useQuery({
		queryKey: ['company-group', groupId],
		queryFn: () => getCompany(groupId),
		enabled: !userLoading,
	});

	const { data: usersData, isLoading: usersLoading } = useQuery({
		queryKey: ['company-users', groupId, searchParams],
		queryFn: () => getCompanyUsers(groupId, query),
		enabled: !userLoading,
	});

	const { data: groupUsers, isLoading: groupUsersLoading } = useQuery({
		queryKey: ['company-users-all', groupId],
		queryFn: () => getCompanyUsers(groupId),
		enabled: !userLoading,
	});

	const { data: roles } = useQuery({
		queryKey: ['company-roles', groupId],
		queryFn: () => getCompanyRoles(groupId),
		enabled: !userLoading,
	});

	const { data: documentTypesResponse } = useQuery({
		queryKey: ['company-document-types'],
		queryFn: () => getDocumentTypes('users'),
	});

	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const defaultCountry = config?.defaultCountry || 'BE';

	// Local state
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [activeUser, setActiveUser] = useState<User | undefined>();
	const [activeUserTasks, setActiveUserTasks] = useState<Task[]>([]);
	const [activeUserProfile, setActiveUserProfile] = useState<Profile>();
	const [activeUserTodoTasks, setActiveUserTodoTasks] = useState<Task[]>([]);
	const [activeUserApprovals, setActiveUserApprovals] = useState<Task[]>([]);
	const [activeUserTodoApprovals, setActiveUserTodoApprovals] = useState<Task[]>([]);
	const [selectedFile, setSelectedFile] = useState<any>(null);
	const [activeUserDocuments, setActiveUserDocuments] = useState<Document[]>([]);
	const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
	const [activeTask, setActiveTask] = useState<Task | undefined>();
	const [isEditingRoles, setIsEditingRoles] = useState(false);
	const [isEditingUser, setIsEditingUser] = useState(false);
	const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
	const [roleToRevokeData, setRoleToRevokeData] = useState<
		| {
				roleId: string;
				roleName: string;
				cascadingRoles: Role[];
		  }
		| undefined
	>();
	const [isRoleDialogOpen, setRoleDialogOpen] = useState(false);
	const [isInviteSent, setIsInviteSent] = useState(false);
	const [isSendingInvite, setIsSendingInvite] = useState(false);
	const [rolesWithTasks, setRolesWithTasks] = useState<RoleWithTasks[]>([]);
	const [archiveDate, setArchiveDate] = useState<Date>(new Date());
	const [archiveMenuAnchor, setArchiveMenuAnchor] = useState<null | HTMLElement>(null);
	const [documentFilterOption, setDocumentFilterOption] = useState('exclude_superseded');

	const documentFilterOptions = [
		{ value: 'exclude_superseded', label: 'Active documents' },
		{ value: 'all', label: 'All documents' },
	];

	useBreadcrumbLevel(1, 'Users');
	const requirePhoneOrEmail = featureFlags?.IS_ALHEEMBOUW || false;
	const allRoles = roles || [];
	const documentTypes = documentTypesResponse?.data || [];
	const data = usersData || { data: [], count: 0, page: 0, pageSize: 25 };
	const verifyPermission = authenticatedUser ? getVerifyPermission(authenticatedUser.permissions) : () => false;

	const isArchived = activeUser?.status === 'archived';
	const isPlannedForArchive = !!activeUser?.plannedArchival?.archiveDate;
	const archiveIsValid = archiveDate >= new Date(new Date().setHours(0, 0, 0, 0));

	useEffect(() => {
		if (!activeUser || !data.data) return;
		const newActiveUser = data.data.find(({ id: userId }: any) => userId === activeUser?.id);
		if (newActiveUser) {
			setActiveUser(newActiveUser);
			setArchiveDate(
				newActiveUser?.plannedArchival?.archiveDate ? new Date(newActiveUser.plannedArchival.archiveDate) : new Date(),
			);
		}
	}, [activeUser, data.data]);

	const orderedRoles = orderBy(allRoles, ['usesManager', 'name']).map((r) => ({
		...r,
		group: r.usesManager ? 'System Role' : 'User Role',
	}));

	const updateUserDocuments = useCallback(
		async (userId: string) => {
			const params = new URLSearchParams();
			if (documentFilterOption === 'exclude_superseded') {
				params.set('filter', 'exclude_superseded');
			}
			params.set('includeLogs', 'true');
			const userDocs = await getCompanyUserDocuments(groupId, userId, params.toString());
			setActiveUserDocuments(userDocs.data);
		},
		[documentFilterOption, groupId],
	);

	useEffect(() => {
		if (activeUser?.id) {
			updateUserDocuments(activeUser.id);
		}
	}, [documentFilterOption, activeUser?.id, updateUserDocuments]);

	if (userLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !group) return null;

	function roleBelongsToGroup(role: any) {
		return group!.possibleRoleIds?.includes(role.id) || group!.type?.possibleRoleIds?.includes(role.id);
	}

	const revalidateUsers = () => {
		queryClient.invalidateQueries({ queryKey: ['company-users', groupId] });
		queryClient.invalidateQueries({ queryKey: ['company-users-all', groupId] });
	};

	const handleSort = ([sort]: any[]) => {
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handleRolesAdd = async (roleIds: string[]) => {
		if (!activeUser) return;
		await request.post(`/api/company-portal/groups/${group.id}/users/${activeUser.id}/roles/batch`, { roleIds });
		setRoleDialogOpen(false);
		revalidateUsers();
	};

	const handleRowClick = async ({ id }: any) => {
		setIsInviteSent(false);
		setIsSendingInvite(false);
		setDocumentFilterOption('exclude_superseded');
		setActiveUser(data.data.find(({ id: userId }: any) => userId === id));
		const [userTasksResponse, userApprovalsResponse, userProfileResponse, userRolesWithTasksResponse] =
			await Promise.all([
				getCompanyUserTasks(groupId, id),
				getCompanyUserApprovals(groupId, id),
				getCompanyUserProfile(groupId, id),
				getCompanyUserRolesWithTasks(groupId, id),
			]);
		const userTasks = userTasksResponse.data;
		setActiveUserTodoTasks(userTasks.filter((task: Task) => task.status === 'todo'));
		setActiveUserTasks(userTasks);
		const userApprovals = userApprovalsResponse.data;
		setActiveUserTodoApprovals(userApprovals.filter((approval: Task) => approval.status === 'todo'));
		setActiveUserApprovals(userApprovals);
		setRolesWithTasks(userRolesWithTasksResponse.data);
		updateUserDocuments(id);
		setActiveUserProfile(userProfileResponse);
		setIsDrawerOpen(true);
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleFilterChange = (filterData: Record<string, any>) => {
		updateQueryParams(filterData);
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleCloseDrawer = () => {
		setActiveUser(undefined);
		setActiveUserTasks([]);
		setRolesWithTasks([]);
		setActiveUserTodoTasks([]);
		setActiveUserApprovals([]);
		setActiveUserTodoApprovals([]);
		setIsDrawerOpen(false);
		setIsEditingRoles(false);
	};

	const handleDeleteUser = async () => {
		if (!activeUser) return;
		const queryParams = new URLSearchParams();
		if (archiveDate) {
			queryParams.append('archiveDate', archiveDate.toISOString().split('T')[0]);
		}
		await request
			.del(`/api/company-portal/groups/${group.id}/users/${activeUser.id}?${queryParams.toString()}`)
			.then((response) => {
				const updatedUser = response.body as User;
				const hasPlannedArchival = !!updatedUser?.plannedArchival?.archiveDate;
				if (hasPlannedArchival && updatedUser?.plannedArchival?.archiveDate) {
					EventEmitter.emit('toast', {
						type: 'success',
						message: `User scheduled for archival on ${new Date(updatedUser.plannedArchival.archiveDate).toLocaleDateString('en-GB')}`,
					});
				} else {
					EventEmitter.emit('toast', { type: 'success', message: 'User archived' });
					setIsDrawerOpen(false);
				}
			})
			.catch(() => {
				EventEmitter.emit('toast', { type: 'error', message: 'Error archiving user' });
			});
		revalidateUsers();
		setIsDeleteUserDialogOpen(false);
	};

	const handleCancelPlannedArchival = async () => {
		if (!activeUser) return;
		try {
			await request.put(`/api/company-portal/groups/${group.id}/users/${activeUser.id}/cancel-planned-archival`, {});
			EventEmitter.emit('toast', { type: 'success', message: 'Planned archival cancelled successfully.' });
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to cancel planned archival.' });
		}
		setArchiveDate(new Date());
		revalidateUsers();
	};

	const updateUserData = async (userData: any) => {
		if (!activeUser) {
			EventEmitter.emit('toast', { type: 'error', message: 'Something went wrong. Refresh the page and try again.' });
			return;
		}
		try {
			await request.put(`/api/company-portal/groups/${group.id}/users/${activeUser.id}/user-data`, userData);
			revalidateUsers();
			setIsEditingUser(false);
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update user.' });
		}
	};

	const handleResendInvite = async () => {
		if (!activeUser) return;
		setIsSendingInvite(true);
		try {
			await request.post(`/api/company-portal/groups/${group.id}/users/${activeUser.id}/resend-invite`, {});
			revalidateUsers();
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to resend invite to user.' });
			return;
		} finally {
			setIsSendingInvite(false);
		}
		setIsInviteSent(true);
	};

	const handleRoleRevoke = async (roleIds: string[]) => {
		if (!activeUser) return;
		await request
			.post(`/api/company-portal/groups/${group.id}/users/${activeUser.id}/roles/revoke-batch`, { roleIds })
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: roleIds.length > 1 ? 'Roles removed' : 'Role removed' });
			})
			.catch(() => {
				EventEmitter.emit('toast', {
					type: 'error',
					message: roleIds.length > 1 ? 'Error deleting roles' : 'Error deleting role',
				});
			});
		revalidateUsers();
	};

	const handleDeleteRole = async (roleId: string) => {
		if (!activeUser) return;
		const role = allRoles.find((r) => r.id === roleId);
		const userRoles = activeUser.roles || [];
		const cascadingRoles = calculateCascadingRoleRevocations(roleId, userRoles, allRoles);
		setRoleToRevokeData({ roleId, roleName: role?.name || roleId, cascadingRoles });
	};

	const handleConfirmRoleRevoke = async () => {
		if (!roleToRevokeData) return;
		const allRoleIds = [roleToRevokeData.roleId, ...roleToRevokeData.cascadingRoles.map((r) => r.id)];
		await handleRoleRevoke(allRoleIds);
		setRoleToRevokeData(undefined);
	};

	const handleTaskStatusClick = (task: Task, isExecutable: boolean) => {
		if (!isExecutable) return;
		setActiveTask(task);
	};

	const handleTaskDialogClose = async () => {
		setActiveTask(undefined);
		if (activeUser) {
			setActiveUserTasks((await getCompanyUserTasks(groupId, activeUser.id)).data);
			setRolesWithTasks((await getCompanyUserRolesWithTasks(groupId, activeUser.id)).data);
		}
		queryClient.invalidateQueries({ queryKey: ['company-tasks', groupId] });
	};

	const handleInviteUser = async (inviteData: InviteData) => {
		try {
			const { body: user } = await request.post(`/api/company-portal/groups/${group.id}/users`, inviteData);
			EventEmitter.emit('toast', { type: 'success', message: 'Saved' });
			revalidateUsers();
			setIsAddUserDialogOpen(false);
			return user;
		} catch (error: any) {
			EventEmitter.emit('toast', { type: 'error', message: error.message || 'Failed to invite user.' });
		}
	};

	const handleImportUsers = async (importData: ImportData) => {
		try {
			const { body: result } = await request.post(`/api/company-portal/groups/${group.id}/users/import`, importData);
			if (result.success) {
				EventEmitter.emit('toast', { type: 'success', message: 'Users imported' });
				revalidateUsers();
				setIsAddUserDialogOpen(false);
			} else {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to import all users.' });
			}
			return result;
		} catch (error: unknown) {
			return importUserFailureFromCaughtError(error);
		}
	};

	const handleTaskSelected = (taskId: string) => {
		if (!activeUser) return;
		const task = activeUserTasks.find((t) => t.id === taskId);
		if (!task) return;
		const isExecutable = task.status === 'todo' && task.executePermission?.isAllowed;
		handleTaskStatusClick(task, !!isExecutable);
	};

	const handleTaskExecution = (task: Task) => {
		if (task.status !== 'todo') return { isExecutable: false, tooltipText: '' };
		if (!task.executePermission?.isAllowed)
			return { isExecutable: false, tooltipText: task.executePermission?.denyReason ?? '' };
		return { isExecutable: true, tooltipText: '' };
	};

	const renderPhoneNumber = () => {
		try {
			if (activeUser?.phone) return parsePhoneNumber(activeUser.phone)?.formatInternational();
			if (activeUser?.userData?.fixedPhoneLine)
				return parsePhoneNumber(activeUser.userData?.fixedPhoneLine)?.formatInternational();
			return '-';
		} catch {
			return activeUser?.phone || activeUser?.userData?.fixedPhoneLine || '-';
		}
	};

	return (
		<>
			<div className="mt-2 flex flex-row flex-wrap gap-2">
				<FilterSearch
					key="search"
					label="Search User"
					defaultValue={searchParams.search || ''}
					onChange={debounce((searchInput: string) => handleSearchChange(searchInput), 350)}
					sx={{ width: '30%', minWidth: '30%', flexGrow: 1 }}
				/>
				<FilterAutoCompleteById
					key="roleFilter"
					label="Filter Roles"
					options={orderedRoles}
					groupBy={(option: any) => orderedRoles.find((r) => r.id === option)?.group}
					multiple
					value={searchParams.roleIds || []}
					onChange={(roleIds: any) => handleFilterChange({ roleIds })}
					sx={{ width: '25%', minWidth: '25%', flexGrow: 1 }}
				/>
				<FilterSelect
					key="roleStatus"
					listSubHeader="Filter Role Status"
					options={roleStatusTypes}
					value={searchParams.roleStatus || 'all'}
					onChange={(roleStatus: any) => handleFilterChange({ roleStatus })}
					sx={{ width: '25%', minWidth: '25%', flexGrow: 1 }}
				/>
				<ConditionalWrapper
					Wrapper={({ children }: any) => (
						<Tooltip text="This action is not available for your role">{children}</Tooltip>
					)}
					condition={!verifyPermission(PERMISSIONS.companyUsersCreate)}
				>
					<Button
						disabled={
							!verifyPermission(PERMISSIONS.companyUsersCreate) ||
							(group?.legalForm === 'selfEmployed' && groupUsers?.count && groupUsers.count >= 2)
						}
						startIcon={<AddIcon />}
						variant="outlined"
						onClick={() => setIsAddUserDialogOpen(true)}
						sx={{ pointerEvents: 'auto !important' }}
					>
						Add user
					</Button>
				</ConditionalWrapper>
			</div>
			<Paper className="mt-2" variant="outlined">
				<DataGrid
					data={data.data}
					columns={baseColumns}
					rowCount={data.count || 0}
					isLoading={usersLoading}
					paginationMode="server"
					sortingMode="server"
					onPaginationModelChange={handlePaginationModelChange}
					onSortModelChange={handleSort}
					sortingOrder={['asc', 'desc']}
					sortModel={[stringToSort(searchParams.sort)]}
					pageSize={data.pageSize}
					pageSizeOptions={[data.pageSize]}
					page={data.page}
					onRowClick={handleRowClick}
					pagination
					getRowClassName={(p: any) => (p.row.markedForReview || p.row.status === 'blocked') && 'error'}
					onReload={revalidateUsers}
				/>
			</Paper>
			<Drawer
				onClose={handleCloseDrawer}
				open={isDrawerOpen}
				anchor="right"
				PaperProps={{
					sx: {
						width: 640,
						maxWidth: '100vw',
						overflowX: 'hidden',
					},
				}}
			>
				{activeUser && (
					<div className="p-4 pt-3 pb-3 flex flex-col gap-2">
						<div className="flex items-center gap-2">
							<IconButton sx={{ borderRadius: 1 }} onClick={handleCloseDrawer}>
								<CloseIcon />
							</IconButton>
							<Typography variant="h4">User Details</Typography>
							<div className="flex-grow" />
							{activeUser.email && verifyPermission(PERMISSIONS.companyUsersEdit) && (
								<Button
									variant="outlined"
									disabled={isInviteSent || isSendingInvite}
									sx={{ marginLeft: 2 }}
									onClick={handleResendInvite}
								>
									{isSendingInvite ? 'Sending Invite…' : isInviteSent ? 'Invite Sent' : 'Resend Invite'}
								</Button>
							)}
							<ConditionalWrapper
								condition={!verifyPermission(PERMISSIONS.companyUsersArchive)}
								Wrapper={({ children }: any) => (
									<Tooltip text="Your role does not allow this action">{children}</Tooltip>
								)}
							>
								<IconButton
									onClick={(e) => setArchiveMenuAnchor(e.currentTarget)}
									disabled={!verifyPermission(PERMISSIONS.companyUsersArchive)}
									sx={{ borderRadius: 1, pointerEvents: 'auto !important' }}
								>
									<MoreVertIcon />
								</IconButton>
							</ConditionalWrapper>
							<Menu
								anchorEl={archiveMenuAnchor}
								open={Boolean(archiveMenuAnchor)}
								onClose={() => setArchiveMenuAnchor(null)}
								anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
								transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							>
								<MenuItem
									onClick={() => {
										setArchiveMenuAnchor(null);
										setIsDeleteUserDialogOpen(true);
									}}
								>
									<ListItemIcon>
										<DeleteIcon />
									</ListItemIcon>
									<ListItemText>Archive User</ListItemText>
								</MenuItem>
								{isPlannedForArchive && !isArchived && (
									<MenuItem
										onClick={() => {
											setArchiveMenuAnchor(null);
											handleCancelPlannedArchival();
										}}
									>
										<ListItemIcon>
											<CancelIcon />
										</ListItemIcon>
										<ListItemText>Cancel planned archival</ListItemText>
									</MenuItem>
								)}
							</Menu>
						</div>
						{activeUserTodoTasks.length > 0 && (
							<Alert severity="warning" variant="filled" sx={{ marginBottom: 2 }}>
								This user has one or more pending tasks.
							</Alert>
						)}
						{activeUserTodoApprovals.length > 0 && (
							<Alert severity="warning" variant="filled" sx={{ marginBottom: 2 }}>
								This user has one or more pending approvals.
							</Alert>
						)}
						{activeUser.status === 'blocked' && (
							<Alert severity="error" variant="filled" sx={{ marginBottom: 2 }}>
								This user matches one or more blocklist rules or the user's identification document originates from a
								blocked country. Contact your administrator
							</Alert>
						)}
						{isPlannedForArchive && !isArchived && (
							<Alert severity="info" variant="filled" sx={{ marginBottom: 2 }}>
								This user is scheduled for archival on{' '}
								{new Date(activeUser.plannedArchival!.archiveDate!).toLocaleDateString('en-GB')}.
							</Alert>
						)}
						{Array.isArray(activeUser.markedForReview) && activeUser.markedForReview.length > 0 && (
							<Alert severity="error" variant="filled" sx={{ marginBottom: 2 }}>
								This user is currently locked and requires to be reviewed before receiving access.
							</Alert>
						)}
						<Paper sx={{ padding: 2, minWidth: 570, display: 'flex' }} variant="outlined">
							<div className="flex w-[200px] justify-self-stretch mr-3">
								<Avatar
									variant="square"
									sx={{
										width: 200,
										height: 200,
										borderRadius: 1,
										borderColor: 'action.disabledBackground',
										borderWidth: 1,
										borderStyle: 'solid',
									}}
									src={
										activeUserProfile?.faceImage?.link
											? `/api/company-portal/groups/${group.id}/users/${activeUserProfile?.user.id}/files/${activeUserProfile?.faceImage?.link.split('/').pop()}`
											: ''
									}
								/>
							</div>
							<div className="flex flex-col gap-1 flex-grow">
								<div className="flex items-center justify-between">
									<Typography variant="h6" mb={0.5} sx={{ fontWeight: 'bold' }}>
										{activeUser.firstName} {activeUser.lastName}
									</Typography>
									<ConditionalWrapper
										condition={!verifyPermission(PERMISSIONS.companyUsersEdit)}
										Wrapper={({ children }: any) => (
											<Tooltip text="Your role does not allow this action">{children}</Tooltip>
										)}
									>
										<ToggleButtonGroup
											value={isEditingUser}
											exclusive
											disabled={!verifyPermission(PERMISSIONS.companyUsersEdit)}
										>
											<ToggleButton
												value={true}
												onClick={() => setIsEditingUser((p) => !p)}
												sx={{ border: 'none !important', pointerEvents: 'auto !important' }}
											>
												<EditIcon sx={{ fontSize: 20 }} />
											</ToggleButton>
										</ToggleButtonGroup>
									</ConditionalWrapper>
								</div>
								<div className="flex flex-col">
									<Typography variant="caption">Email</Typography>
									<Typography variant="subtitle2" sx={{ minHeight: 24 }}>
										{activeUser.email || '-'}
									</Typography>
								</div>
								<div className="flex flex-col">
									<Typography variant="caption">Phone</Typography>
									<Typography variant="subtitle2" sx={{ minHeight: 24 }}>
										{renderPhoneNumber()}
									</Typography>
								</div>
							</div>
						</Paper>
						<Paper className="p-4" variant="outlined" sx={{ maxWidth: '100%' }}>
							<div className="flex items-center justify-between">
								<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} mb={1}>
									Roles
								</Typography>
								<ConditionalWrapper
									condition={!verifyPermission(PERMISSIONS.companyUsersEdit)}
									Wrapper={({ children }: any) => (
										<Tooltip text="Your role does not allow this action">{children}</Tooltip>
									)}
								>
									<ToggleButtonGroup
										value={isEditingRoles}
										exclusive
										disabled={!verifyPermission(PERMISSIONS.companyUsersEdit)}
									>
										<ToggleButton
											value={true}
											onClick={() => setIsEditingRoles((p) => !p)}
											sx={{ border: 'none !important', pointerEvents: 'auto !important' }}
										>
											<EditIcon sx={{ fontSize: 20 }} />
										</ToggleButton>
									</ToggleButtonGroup>
								</ConditionalWrapper>
							</div>
							<div className="flex flex-wrap gap-1 max-w-full">
								{activeUser.roles.length > 0 || isEditingRoles
									? activeUser.roles.map((role: any) => {
											const canDelete = isEditingRoles && roleBelongsToGroup(role);
											const cannotDelete = isEditingRoles && !roleBelongsToGroup(role);
											return (
												<RoleChip
													key={role.name}
													role={role}
													onDelete={canDelete ? () => handleDeleteRole(role.id) : undefined}
													tooltipCondition={cannotDelete}
													tooltipText="Role cannot be deleted. It does not belong to this company."
												/>
											);
										})
									: '-'}
								{isEditingRoles && (
									<Badge onClick={() => setRoleDialogOpen(true)}>
										<PlusIcon size={12} /> Add Roles
									</Badge>
								)}
							</div>
						</Paper>
						<Paper className="p-4" variant="outlined">
							<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} mb={1}>
								Tasks by User Role
							</Typography>
							<div className="flex flex-col gap-1">
								{rolesWithTasks.length > 0 ? (
									<UserRolesWithTasksAccordion
										rolesWithTasks={rolesWithTasks}
										onTaskSelected={handleTaskSelected}
										onReload={revalidateUsers}
										searchParams={searchParams as Record<string, string | undefined>}
										expandRoles={false}
										hideDetailColumns={true}
									/>
								) : (
									'-'
								)}
							</div>
						</Paper>
						{activeUserApprovals.length > 0 && (
							<Paper className="p-4" variant="outlined">
								<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} mb={1}>
									Approvals
								</Typography>
								<div className="flex flex-col gap-1">
									{activeUserApprovals.map((approval) => {
										const { isExecutable, tooltipText } = handleTaskExecution(approval);
										return (
											<div className="flex justify-between" key={approval.id}>
												<Typography variant="subtitle2">{approval.name}</Typography>
												<ConditionalWrapper
													condition={!isExecutable}
													Wrapper={({ children }: any) => <Tooltip text={tooltipText}>{children}</Tooltip>}
												>
													<div>
														<Badge
															variant={variants[approval.status] as any}
															onClick={() => handleTaskStatusClick(approval, isExecutable)}
														>
															{approval.name}
														</Badge>
													</div>
												</ConditionalWrapper>
											</div>
										);
									})}
								</div>
							</Paper>
						)}
						{verifyPermission(PERMISSIONS.companyUsersView) && (
							<Paper sx={{ padding: 2 }} variant="outlined">
								<div className="flex p-1 px-1 gap-5 items-center">
									<Typography variant="subtitle1" sx={{ fontWeight: 'bold', flexShrink: 0 }}>
										Documents
									</Typography>
									<div className="flex-grow">
										<FilterSelect
											key="type"
											listSubHeader="Filter Documents"
											options={documentFilterOptions}
											value={documentFilterOption}
											onChange={(f: string) => setDocumentFilterOption(f)}
											size="small"
											sx={{ width: 160, height: '23px' }}
										/>
									</div>
								</div>
								<div className="flex flex-col gap-1">
									{activeUserDocuments.length > 0
										? activeUserDocuments.map((document) => (
												<Button
													key={document.id}
													sx={{ display: 'flex', alignItems: 'center', textTransform: 'initial', gap: 1 }}
													onClick={() => setSelectedFile(document)}
												>
													<FileState status={document.status} />
													<Typography variant="subtitle2" sx={{ flexGrow: 1, textAlign: 'left' }}>
														{document.fileName}
													</Typography>
													<Typography variant="subtitle2">
														{documentTypes.find((dt: any) => dt.id === document.type)?.name || document.type}
													</Typography>
												</Button>
											))
										: '-'}
								</div>
							</Paper>
						)}
					</div>
				)}
			</Drawer>
			<TaskDialog task={activeTask} onClose={handleTaskDialogClose} forCompanyPortal={true} userId={activeUser?.id ?? ''} />
			<InviteForm
				onClose={() => {
					revalidateUsers();
					setIsAddUserDialogOpen(false);
				}}
				isOpen={isAddUserDialogOpen}
				onInvite={handleInviteUser}
				onImport={handleImportUsers}
				roles={allRoles}
				selectedGroup={group}
				authenticatedUser={authenticatedUser}
				defaultCountry={defaultCountry || 'BE'}
				permissions={{
					canCreateWithoutEmail: false,
					isUserAdmin: false,
					canImport: group?.legalForm !== 'selfEmployed',
				}}
				requirePhoneOrEmail={requirePhoneOrEmail}
			/>
			<ConfirmDialog
				isOpen={!!roleToRevokeData}
				cancelAction={() => setRoleToRevokeData(undefined)}
				confirmAction={handleConfirmRoleRevoke}
				cancelLabel="Cancel"
				confirmLabel="Delete"
			>
				Are you sure you want to delete the role <strong>{roleToRevokeData?.roleName}</strong>?
			</ConfirmDialog>
			{activeUser && (
				<UserForm
					isOpen={isEditingUser}
					isAdmin={false}
					onClose={() => setIsEditingUser(false)}
					onSubmit={updateUserData}
					data={activeUser}
					authenticatedUser={authenticatedUser}
					defaultCountry={defaultCountry || 'BE'}
				/>
			)}
			{activeUser && (
				<FormDialog
					isOpen={isDeleteUserDialogOpen}
					onClose={() => {
						setIsDeleteUserDialogOpen(false);
						setArchiveDate(
							activeUser?.plannedArchival?.archiveDate ? new Date(activeUser.plannedArchival.archiveDate) : new Date(),
						);
					}}
					title="Archive User"
					description="Are you sure you want to archive this user?"
					buttons={
						<>
							<Button
								variant="outlined"
								onClick={() => {
									setIsDeleteUserDialogOpen(false);
									setArchiveDate(
										activeUser?.plannedArchival?.archiveDate
											? new Date(activeUser.plannedArchival.archiveDate)
											: new Date(),
									);
								}}
							>
								Cancel
							</Button>
							<Button variant="contained" color="error" disabled={!archiveIsValid} onClick={handleDeleteUser}>
								Archive
							</Button>
						</>
					}
				>
					<Tooltip text="The user will be archived on the selected date at 00:00 AM. If today is selected, the user will be archived immediately.">
						<div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
							<DialogContentText sx={{ mb: 0 }}>User will be archived on:</DialogContentText>
							<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
								<DatePicker
									value={archiveDate}
									onChange={(d: any) => setArchiveDate(d)}
									minDate={new Date()}
									slotProps={{ textField: { size: 'small' } }}
								/>
							</LocalizationProvider>
						</div>
					</Tooltip>
				</FormDialog>
			)}
			{activeUser && (
				<AddToRoleDialog
					isOpen={isRoleDialogOpen}
					onClose={() => setRoleDialogOpen(false)}
					userData={activeUser}
					roles={allRoles}
					onRolesAdd={handleRolesAdd}
				/>
			)}
			{activeUser && selectedFile && (
				<DocumentViewer
					document={selectedFile}
					onClose={() => setSelectedFile(null)}
					types={documentTypes}
					apiUrlBase={`/api/company-portal/groups/${group.id}/users/${activeUser.id}/files`}
					onRevalidateDocument={() => updateUserDocuments(activeUser.id)}
					tasks={[]}
					permissions={{
						metadata: verifyPermission([PERMISSIONS.companyUsersView]),
						fullView: verifyPermission([PERMISSIONS.companyUsersView]),
						sensitiveView: false,
						sensitiveEdit: false,
						sensitiveArchive: false,
						edit: false,
						archive: false,
						review: false,
						enableReview: false,
					}}
				/>
			)}
		</>
	);
}
